import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import 'react-quill-new/dist/quill.snow.css';
import { boardAPI, postAPI, uploadAPI } from '../../api';
import { useAuth } from '../../contexts/AuthContext';
import ReactQuill from 'react-quill-new';
import { usePost } from '../../contexts/PostContext';
// ReactQuill을 지연 로딩합니다
// const ReactQuill = lazy(() => import('react-quill-new'));

const PostCreateContainer = styled.div`
  width: 100%;
  background-color: #fff;
  padding: ${({ theme }) => theme.spacing.lg};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const PageTitle = styled.h1`
  font-size: ${({ theme }) => theme.fontSizes.xxl};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  color: ${({ theme }) => theme.colors.text};
`;

const FormGroup = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const Label = styled.label`
  display: block;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-family: inherit;
`;

const Select = styled.select`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-family: inherit;
  background-color: white;
`;

const EditorContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  
  .quill {
    border-radius: 4px;
    border: 1px solid ${({ theme }) => theme.colors.border};
  }
  
  .ql-editor {
    min-height: 300px;
    font-size: ${({ theme }) => theme.fontSizes.md};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.md};
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  background-color: ${({ theme, variant }) => 
    variant === 'primary' ? theme.colors.primary : 'white'};
  color: ${({ theme, variant }) => 
    variant === 'primary' ? 'white' : theme.colors.text};
  border: 1px solid ${({ theme, variant }) => 
    variant === 'primary' ? theme.colors.primary : theme.colors.border};

  &:hover {
    background-color: ${({ theme, variant }) => 
      variant === 'primary' ? theme.colors.info : theme.colors.light};
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const LoadingText = styled.div`
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  font-weight: bold;
`;

interface Board {
  name: string;
  board_id: number;
  id?: number; // 일부 응답에서는 id로 사용될 수 있음
}

const PostCreate: React.FC = () => {
  const {fetchPostsByBoard} = usePost();
  const navigate = useNavigate();
  const location = useLocation();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [selectedBoard, setSelectedBoard] = useState('');
  const [boards, setBoards] = useState<Board[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editorReady, setEditorReady] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const quillRef = useRef<any>(null);
  
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (!isAuthenticated) {
      alert('로그인이 필요합니다.');
      navigate('/login');
      return;
    }

    // URL에서 boardId 파라미터 가져오기
    const params = new URLSearchParams(location.search);
    const boardIdParam = params.get('boardId');

    const fetchBoards = async () => {
      try {
        const boardsData = await boardAPI.getBoards();
        const boards = boardsData.data.boards.boards;
        setBoards(boards);
        
        // URL에 boardId가 있으면 해당 게시판 선택
        if (boardIdParam && boards.some((board: Board) => board.board_id.toString() === boardIdParam)) {
          setSelectedBoard(boardIdParam);
        } else if (boards.length > 0) {
          // 첫 번째 게시판 선택 (board_id 필드 사용)
          const firstBoardId = boards[0].board_id.toString();
          setSelectedBoard(firstBoardId);
        }
        
        // 에디터 준비 완료 상태로 설정
        setEditorReady(true);
      } catch (error) {
        console.error('게시판 목록 로딩 오류:', error);
      }
    };

    fetchBoards();
  }, [navigate, isAuthenticated, location.search]);

  useEffect(() => {
    if (quillRef.current) {
      // 에디터가 마운트된 후 이미지 핸들러 등록
      const quill = quillRef.current.getEditor();
      
      // 이미지 핸들러 설정
      const toolbar = quill.getModule('toolbar');
      toolbar.addHandler('image', () => {
        // 파일 선택 다이얼로그 생성
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/jpeg,image/png,image/gif,image/jpg');
        input.click();

        // 파일 선택 이벤트 처리
        input.onchange = async () => {
          if (input.files && input.files.length > 0) {
            const file = input.files[0];
            setIsUploading(true);
            
            try {
              const response = await uploadAPI.uploadImage(file);
              const imageUrl = response.data.url;
              
              
              // 현재 커서 위치에 이미지 삽입
              const range = quill.getSelection(true);
              quill.insertEmbed(range.index, 'image', imageUrl);
              quill.setSelection(range.index + 1);
            } catch (error) {
              console.error('이미지 업로드 오류:', error);
              alert('이미지 업로드 중 오류가 발생했습니다.');
            } finally {
              setIsUploading(false);
            }
          }
        };
      });
    }
  }, [quillRef.current]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim() || !content.trim() || !selectedBoard) {
      alert('제목, 내용, 게시판을 모두 입력해주세요.');
      return;
    }

    // 로컬 스토리지에서 토큰 확인
    const token = localStorage.getItem('token');
    if (!token) {
      alert('로그인이 필요합니다. 다시 로그인해주세요.');
      navigate('/login');
      return;
    }

    setIsSubmitting(true);

    try {
      // URL 파라미터로 받은 게시판 ID가 있으면 해당 게시판 우선 사용
      const params = new URLSearchParams(location.search);
      const boardIdParam = params.get('boardId');
      
      // 실제 요청에 사용할 게시판 ID 선택
      const finalBoardId = boardIdParam || selectedBoard;
      
      const postData = {
        title,
        content,
        board_id: parseInt(finalBoardId, 10)
      };
      
      const newPost = await postAPI.createPost(postData);
      
      fetchPostsByBoard(parseInt(finalBoardId, 10));
      navigate(`/posts/${newPost.data.post.post_id}`);
    } catch (error: any) {
      console.error('게시글 작성 오류:', error);
      
      if (error.response?.status === 401) {
        alert('인증이 만료되었습니다. 다시 로그인해주세요.');
        navigate('/login');
      } else if (error.response?.status === 403) {
        alert(`권한 오류: ${error.response?.data?.message || '해당 게시판에 글을 작성할 권한이 없습니다.'}`);
        // 권한 문제의 경우 자유게시판(board_id=2)으로 변경 시도
        try {
          const retryData = {
            title,
            content,
            board_id: 2 // 자유게시판으로 시도
          };
          const newPost = await postAPI.createPost(retryData);
          navigate(`/posts/${newPost.data.post.id}`);
        } catch (retryError) {
          console.error('자유게시판 재시도 실패:', retryError);
          alert('모든 게시판에 글을 작성할 수 없습니다. 관리자에게 문의하세요.');
        }
      } else {
        alert(`게시글 작성에 실패했습니다: ${error.response?.data?.message || '알 수 없는 오류'}`);
      }
      
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const modules = {
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        ['link', 'image'],
        ['clean']
      ],
    }
  };

  return (
    <PostCreateContainer>
      {isUploading && (
        <LoadingOverlay>
          <LoadingText>이미지 업로드 중...</LoadingText>
        </LoadingOverlay>
      )}
      
      <PageTitle> 
        {boards.find(board => board.board_id.toString() === selectedBoard)?.name || '게시판'} 
        {" > "} 새 게시글 작성
      </PageTitle>
      
      <form onSubmit={handleSubmit}>
        {/* <FormGroup style={{ display: "block" }}>
          <Label htmlFor="board">게시판</Label>
          <Select
            id="board"
            value={selectedBoard}
            onChange={(e) => setSelectedBoard(e.target.value)}
            required
          >
            <option value="">게시판을 선택해주세요</option>
            {boards.map((board: Board) => (
              <option key={board.board_id} value={board.board_id.toString()}>
                {board.name}
              </option>
            ))}
          </Select>
        </FormGroup> */}
        
        <FormGroup>
          <Label htmlFor="title">제목</Label>
          <Input
            id="title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="제목을 입력해주세요"
            required
          />
        </FormGroup>
        
        <EditorContainer>
          <Label htmlFor="content">내용</Label>
          {editorReady && (
            <Suspense fallback={<div>에디터 로딩 중...</div>}>
              <ReactQuill 
                ref={quillRef}
                theme="snow" 
                value={content} 
                onChange={setContent}
                modules={modules}
                placeholder="내용을 입력해주세요 (툴바의 이미지 버튼을 클릭하여 이미지를 추가할 수 있습니다)"
              />
            </Suspense>
          )}
        </EditorContainer>
        
        <ButtonGroup>
          <Button type="button" onClick={handleCancel}>취소</Button>
          <Button type="submit" variant="primary" disabled={isSubmitting || isUploading}>
            {isSubmitting ? '등록 중...' : '등록하기'}
          </Button>
        </ButtonGroup>
      </form>
    </PostCreateContainer>
  );
};

export default PostCreate; 